import { jsx, jsxs } from "react/jsx-runtime";
import { FillIcon, DeleteSmallIcon } from "@dion-web/ui-icons";
import MBadge from "@mui/material/Badge";
import MBox from "@mui/material/Box";
import styled from "@mui/material/styles/styled";
import isFunction from "lodash/isFunction";
import toNumber from "lodash/toNumber";
import { useParentBg } from "../../shared/hooks/current-color";
import {
  calculatePosition,
  containsGradient,
  getInscribedSquareSide
} from "./helpers";
import AvatarSimple from "../AvatarSimple";
import { Icon } from "../Icon";
import { useTheme } from "../ThemeProvider";
var EAvatarSize = /* @__PURE__ */ ((EAvatarSize2) => {
  EAvatarSize2["XS"] = "xs";
  EAvatarSize2["S"] = "s";
  EAvatarSize2["M"] = "m";
  EAvatarSize2["L"] = "l";
  EAvatarSize2["XL"] = "xl";
  EAvatarSize2["XXL"] = "xxl";
  EAvatarSize2["XXXL"] = "xxxl";
  return EAvatarSize2;
})(EAvatarSize || {});
var EAvatarVariant = /* @__PURE__ */ ((EAvatarVariant2) => {
  EAvatarVariant2["ROUNDED"] = "rounded";
  EAvatarVariant2["SQUARE"] = "square";
  return EAvatarVariant2;
})(EAvatarVariant || {});
var EAvatarColors = /* @__PURE__ */ ((EAvatarColors2) => {
  EAvatarColors2["ERROR"] = "error";
  EAvatarColors2["INFO"] = "info";
  EAvatarColors2["SUCCESS"] = "success";
  EAvatarColors2["WARNING"] = "warning";
  EAvatarColors2["ELEVATION_0"] = "elevation0";
  EAvatarColors2["ELEVATION_1"] = "elevation1";
  EAvatarColors2["ELEVATION_2"] = "elevation2";
  EAvatarColors2["ELEVATION_3"] = "elevation3";
  EAvatarColors2["ON_BRAND"] = "onBrand";
  EAvatarColors2["BRAND"] = "brand";
  EAvatarColors2["BLUE_ELEVATION_1"] = "blueElevation1";
  EAvatarColors2["BLUE_ELEVATION_2"] = "blueElevation2";
  return EAvatarColors2;
})(EAvatarColors || {});
const POSITION_TOP_RIGHT = ["top", "right"];
const POSITION_BOTTOM_RIGHT = ["bottom", "right"];
const CLOSE_POSITION_RATIO = [1.55, 0.85];
const ONLINE_POSITION_RATIO = [2, 1];
const ICONS = {
  fill: FillIcon,
  delete_small: DeleteSmallIcon
};
const AvatarContainer = styled(MBox)(({ size }) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: size,
    minHeight: size,
    maxWidth: size,
    maxHeight: size
  };
});
const ControlSubstrate = styled(MBox)(({ size, bgColor, controlPosition }) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    minWidth: `${size}px`,
    minHeight: `${size}px`,
    maxWidth: `${size}px`,
    maxHeight: `${size}px`,
    backgroundColor: bgColor,
    position: "absolute",
    ...controlPosition
  };
});
const ControlContainer = styled(MBox)(({ bgColor, size, iconColor, cursor }) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    minWidth: `${size}px`,
    minHeight: `${size}px`,
    maxWidth: `${size}px`,
    maxHeight: `${size}px`,
    backgroundColor: bgColor,
    position: "absolute",
    color: iconColor,
    cursor
  };
});
const ControlPosition = styled(MBox)(({ size }) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: size,
    minHeight: size,
    maxWidth: size,
    maxHeight: size,
    position: "relative"
  };
});
const CounterContainer = styled(MBox)(({ size, controlPosition }) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    minWidth: `${size}px`,
    minHeight: `${size}px`,
    maxWidth: `${size}px`,
    maxHeight: `${size}px`,
    position: "absolute",
    ...controlPosition
  };
});
const AvatarCounter = styled(MBadge)(({ theme, size, color, borderRadius, borderSize, borderColor, typography }) => {
  return {
    "& .MuiBadge-badge": {
      backgroundColor: theme.palette[color].main,
      height: `${size}px`,
      minWidth: `${size}px`,
      color: theme.palette[color].contrast,
      borderRadius: `${borderRadius}px`,
      border: `${borderSize}px solid ${borderColor}`,
      ...typography
    }
  };
});
const IconBase = (props) => {
  const {
    borderColor,
    onClick,
    controlSize,
    controlContainerBgColor,
    controlIconColor,
    containerCursor,
    controlIcon,
    controlIconSize,
    controlPosition,
    controlBorderSize
  } = props;
  const controlSubstrateSize = controlSize + toNumber(controlBorderSize) * 2;
  return /* @__PURE__ */ jsx(
    ControlSubstrate,
    {
      borderSize: controlBorderSize,
      size: controlSubstrateSize,
      controlPosition,
      bgColor: borderColor,
      onClick,
      cursor: containerCursor,
      children: /* @__PURE__ */ jsx(
        ControlContainer,
        {
          size: controlSize,
          bgColor: controlContainerBgColor,
          iconColor: controlIconColor,
          onClick,
          cursor: containerCursor,
          children: /* @__PURE__ */ jsx(Icon, { icon: controlIcon, size: controlIconSize })
        }
      )
    }
  );
};
const CustomIconContainer = (props) => {
  const {
    iconDefaults,
    theme,
    bg,
    icon: IconElement,
    size,
    borderColor,
    iconColor,
    iconBgColor,
    isSquare,
    position,
    positionRatio,
    onClick
  } = props;
  const containerCursor = onClick ? "pointer" : "";
  const IconComponent = ICONS[iconDefaults.icon] ?? FillIcon;
  const controlIcon = IconElement ?? /* @__PURE__ */ jsx(IconComponent, {});
  const controlContainerBgColor = theme.palette[bg][iconBgColor] || iconBgColor || theme.palette.elevation2.main;
  const controlIconColor = theme.palette[bg][iconColor] || iconColor;
  const controlSize = toNumber(iconDefaults.size[size]);
  const controlIconSize = iconDefaults.iconSize[size];
  const positionArgs = [...position, controlSize, isSquare, positionRatio];
  const controlPosition = calculatePosition.apply({}, positionArgs);
  const controlBorderSize = iconDefaults?.border?.[size];
  return /* @__PURE__ */ jsx(
    IconBase,
    {
      borderColor,
      controlContainerBgColor,
      controlIconColor,
      controlBorderSize,
      onClick,
      controlSize,
      controlPosition,
      containerCursor,
      controlIcon,
      controlIconSize
    }
  );
};
const CounterIconContainer = ({ bg, theme, borderColor, size, isSquare, counter }) => {
  const counterControlBorderSize = theme.avatar.controls.counter.border[size];
  const counterControlSize = toNumber(theme.avatar.controls.counter.size[size]) + toNumber(counterControlBorderSize) * 2;
  const counterPosition = calculatePosition("bottom", "right", counterControlSize, isSquare);
  const counterTypography = theme.typography[theme.avatar.controls.counter.typography[size]];
  const counterBorderRadius = theme.avatar.controls.counter.radius[size];
  return /* @__PURE__ */ jsx(
    CounterContainer,
    {
      size: counterControlSize,
      controlPosition: counterPosition,
      children: /* @__PURE__ */ jsx(
        AvatarCounter,
        {
          badgeContent: counter,
          size: counterControlSize,
          borderSize: counterControlBorderSize,
          borderColor,
          color: bg === "brand" ? "onBrand" : "brand",
          typography: counterTypography,
          borderRadius: counterBorderRadius
        }
      )
    }
  );
};
const Avatar = (props) => {
  const {
    size = "l" /* L */,
    variant = "rounded" /* ROUNDED */,
    bg: propsBg,
    isOnline,
    onClose,
    counter,
    slotIcon,
    slotIconColor,
    topRightIconColor,
    ref,
    className
  } = props;
  const theme = useTheme();
  const avatarContainerHeight = theme.avatar.size.avatar[size];
  const controlPositionSquareSize = getInscribedSquareSide(avatarContainerHeight);
  const parentBg = useParentBg();
  const bg = propsBg ?? parentBg ?? "elevation0";
  const blackBorderColor = theme.palette.static.dark;
  const calculatedBorderColor = theme.palette[bg].main;
  const controlContainerBorderColor = containsGradient(calculatedBorderColor) ? blackBorderColor : calculatedBorderColor;
  const showBlackBorderForWaitingIcon = bg === "warning" /* WARNING */;
  const waitingIconBorderColor = showBlackBorderForWaitingIcon ? blackBorderColor : controlContainerBorderColor;
  const isSquare = variant === "square" /* SQUARE */;
  const showOnlineStatus = isOnline && !isFunction(onClose);
  const showCounter = counter > 0 && counter;
  const showSlotIcon = slotIcon && !showCounter;
  const onlineStatusComputedIconColor = topRightIconColor || "successContent";
  const slotComputedIconColor = slotIconColor || "contrast";
  return /* @__PURE__ */ jsx(AvatarContainer, { size: avatarContainerHeight, ref, children: /* @__PURE__ */ jsxs(ControlPosition, { size: controlPositionSquareSize, children: [
    /* @__PURE__ */ jsx(AvatarSimple, { ...props, bg, className }),
    showOnlineStatus && /* @__PURE__ */ jsx(
      CustomIconContainer,
      {
        theme,
        bg,
        borderColor: controlContainerBorderColor,
        size,
        isSquare,
        position: POSITION_TOP_RIGHT,
        positionRatio: ONLINE_POSITION_RATIO,
        iconColor: onlineStatusComputedIconColor,
        iconBgColor: controlContainerBorderColor,
        iconDefaults: theme.avatar.controls.online
      }
    ),
    isFunction(onClose) && /* @__PURE__ */ jsx(
      CustomIconContainer,
      {
        theme,
        bg,
        borderColor: controlContainerBorderColor,
        size,
        isSquare,
        position: POSITION_TOP_RIGHT,
        positionRatio: CLOSE_POSITION_RATIO,
        iconColor: "contrast",
        iconBgColor: theme.palette[bg].transparent,
        iconDefaults: theme.avatar.controls.close,
        onClick: onClose
      }
    ),
    showSlotIcon && /* @__PURE__ */ jsx(
      CustomIconContainer,
      {
        theme,
        bg,
        borderColor: controlContainerBorderColor,
        size,
        isSquare,
        position: POSITION_BOTTOM_RIGHT,
        icon: slotIcon,
        iconColor: slotComputedIconColor,
        iconBgColor: theme.palette[bg].transparent,
        iconDefaults: theme.avatar.controls.slot
      }
    ),
    showCounter && /* @__PURE__ */ jsx(
      CounterIconContainer,
      {
        bg,
        theme,
        borderColor: controlContainerBorderColor,
        size,
        isSquare,
        counter
      }
    )
  ] }) });
};
var Avatar_default = Avatar;
export {
  EAvatarColors,
  EAvatarSize,
  EAvatarVariant,
  Avatar_default as default
};
